import { useEffect, useRef, useState } from 'react';
import {
    Camera,
    Image as ImageIcon,
    X,
    Sparkle,
    Check,
    ArrowCounterClockwise,
} from '@phosphor-icons/react';
import { api, formatApiErrorDetail, tokenStore } from '@/lib/api';
import { SCAN } from '@/constants/testIds';

const CATEGORIES = [
    'chest',
    'back',
    'legs',
    'shoulders',
    'arms',
    'core',
    'cardio',
];
const EQUIPMENT = ['machine', 'barbell', 'dumbbell', 'cable', 'bodyweight', 'other'];

async function compressFile(file, maxDim = 1280, quality = 0.85) {
    // Client-side: resize huge phone photos before upload.
    if (!file.type.startsWith('image/')) return file;
    const bmp = await createImageBitmap(file).catch(() => null);
    if (!bmp) return file;
    const scale = Math.min(1, maxDim / Math.max(bmp.width, bmp.height));
    const w = Math.round(bmp.width * scale);
    const h = Math.round(bmp.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bmp, 0, 0, w, h);
    const blob = await new Promise((res) =>
        canvas.toBlob(res, 'image/jpeg', quality)
    );
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
        type: 'image/jpeg',
    });
}

/**
 * Modal that walks the user through: pick photo → analyze → review → commit
 * as a new custom Exercise. Calls `onCommitted(exercise, weight_reading)` once
 * the exercise has been created. `weight_reading` is the suggested weight (if
 * the model could read it), so the caller can auto-fill in an active session.
 */
export function ScanMachine({ open, onClose, onCommitted, autofillContext }) {
    const [stage, setStage] = useState('pick'); // pick | analyzing | review
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [scan, setScan] = useState(null); // {extraction, image_path, image_url}
    const [form, setForm] = useState(null);
    const [committing, setCommitting] = useState(false);
    const [error, setError] = useState('');
    const cameraRef = useRef(null);
    const fileRef = useRef(null);

    useEffect(() => {
        if (!open) {
            setStage('pick');
            setFile(null);
            setPreview(null);
            setScan(null);
            setForm(null);
            setError('');
            setCommitting(false);
        }
    }, [open]);

    useEffect(() => {
        return () => {
            if (preview) URL.revokeObjectURL(preview);
        };
    }, [preview]);

    function pickFile(f) {
        if (!f) return;
        if (preview) URL.revokeObjectURL(preview);
        setFile(f);
        setPreview(URL.createObjectURL(f));
        setError('');
    }

    async function analyze() {
        if (!file) return;
        setError('');
        setStage('analyzing');
        try {
            const compressed = await compressFile(file);
            const fd = new FormData();
            fd.append('file', compressed);
            const { data } = await api.post('/scan/exercise', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 60000,
            });
            setScan(data);
            const ex = data.extraction;
            setForm({
                name: ex.exercise_name || ex.machine_name || '',
                category: ex.category,
                equipment: ex.equipment,
                muscles: (ex.muscle_groups || []).join(', '),
                weight: ex.weight_reading ?? '',
            });
            setStage('review');
        } catch (e) {
            setError(
                formatApiErrorDetail(e.response?.data?.detail) ||
                    e.message ||
                    'Analysis failed.'
            );
            setStage('pick');
        }
    }

    async function commit() {
        if (!scan || !form?.name) return;
        setCommitting(true);
        setError('');
        try {
            const muscles = form.muscles
                .split(',')
                .map((m) => m.trim())
                .filter(Boolean);
            const payload = {
                image_path: scan.image_path,
                name: form.name.trim(),
                category: form.category,
                equipment: form.equipment,
                muscle_groups: muscles,
            };
            const { data } = await api.post('/scan/commit', payload);
            const weight =
                form.weight === '' || form.weight == null
                    ? null
                    : Number(form.weight);
            onCommitted?.(data, Number.isFinite(weight) ? weight : null);
            onClose?.();
        } catch (e) {
            setError(
                formatApiErrorDetail(e.response?.data?.detail) || e.message
            );
        } finally {
            setCommitting(false);
        }
    }

    if (!open) return null;

    return (
        <div
            data-testid={SCAN.root}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
        >
            <div
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={onClose}
            />
            <div className="relative w-full sm:max-w-xl max-h-[92vh] sm:max-h-[88vh] rounded-t-3xl sm:rounded-3xl glass-card grain flex flex-col overflow-hidden animate-fade-up">
                <Header onClose={onClose} stage={stage} />

                <div className="flex-1 overflow-y-auto p-5">
                    {stage === 'pick' && (
                        <PickStage
                            preview={preview}
                            cameraRef={cameraRef}
                            fileRef={fileRef}
                            onPick={pickFile}
                            onClear={() => {
                                setFile(null);
                                if (preview) URL.revokeObjectURL(preview);
                                setPreview(null);
                            }}
                        />
                    )}

                    {stage === 'analyzing' && (
                        <AnalyzingStage preview={preview} />
                    )}

                    {stage === 'review' && scan && form && (
                        <ReviewStage
                            scan={scan}
                            form={form}
                            setForm={setForm}
                            autofillContext={autofillContext}
                        />
                    )}

                    {error && (
                        <p
                            data-testid={SCAN.errorMessage}
                            className="mt-4 text-sm text-[#FF4D4D] font-medium"
                        >
                            {error}
                        </p>
                    )}
                </div>

                <Footer
                    stage={stage}
                    canAnalyze={!!file}
                    canCommit={!!form?.name && !!scan && !committing}
                    committing={committing}
                    onAnalyze={analyze}
                    onCommit={commit}
                    onRetake={() => {
                        setStage('pick');
                        setScan(null);
                        setForm(null);
                    }}
                    onCancel={onClose}
                />
            </div>
        </div>
    );
}

function Header({ onClose, stage }) {
    const label =
        stage === 'pick'
            ? 'Take or upload a photo'
            : stage === 'analyzing'
              ? 'Analyzing…'
              : 'Review & save';
    return (
        <div className="flex items-center justify-between gap-3 p-5 border-b border-foreground/10">
            <div>
                <p className="uppercase-mono text-muted-foreground">
                    Scan a machine
                </p>
                <h3 className="font-display font-extrabold tracking-tight text-2xl">
                    {label}
                </h3>
            </div>
            <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="grid place-items-center w-10 h-10 rounded-full border border-foreground/10 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.96] transition-all"
            >
                <X size={18} weight="bold" />
            </button>
        </div>
    );
}

function PickStage({ preview, cameraRef, fileRef, onPick, onClear }) {
    return (
        <div className="space-y-4">
            {preview ? (
                <div className="relative rounded-2xl overflow-hidden border border-foreground/10 bg-black">
                    <img
                        data-testid={SCAN.previewImage}
                        src={preview}
                        alt="Selected machine"
                        className="w-full max-h-[55vh] object-contain"
                    />
                    <button
                        type="button"
                        onClick={onClear}
                        className="absolute top-2 right-2 grid place-items-center w-9 h-9 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
                        aria-label="Clear photo"
                    >
                        <X size={16} weight="bold" />
                    </button>
                </div>
            ) : (
                <div className="rounded-2xl border border-dashed border-foreground/20 px-6 py-10 text-center">
                    <span className="grid place-items-center w-12 h-12 rounded-2xl bg-foreground/5 border border-foreground/10 mx-auto mb-4">
                        <Camera size={20} weight="bold" />
                    </span>
                    <p className="font-display font-bold text-lg">
                        Snap the machine or upload a photo
                    </p>
                    <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                        Tip: get the brand label or the weight pin in frame
                        and the model will read both.
                    </p>
                </div>
            )}

            <div className="grid grid-cols-2 gap-3">
                <button
                    type="button"
                    onClick={() => cameraRef.current?.click()}
                    className="h-12 rounded-2xl bg-primary text-primary-foreground font-bold tracking-tight shadow-neon hover:-translate-y-[2px] active:scale-[0.98] transition-all inline-flex items-center justify-center gap-2"
                >
                    <Camera size={18} weight="bold" />
                    Take photo
                </button>
                <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="h-12 rounded-2xl border border-foreground/15 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.98] transition-all font-semibold inline-flex items-center justify-center gap-2"
                >
                    <ImageIcon size={18} weight="bold" />
                    From gallery
                </button>
            </div>

            <input
                ref={cameraRef}
                data-testid={SCAN.cameraInput}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                className="hidden"
                onChange={(e) => onPick(e.target.files?.[0])}
            />
            <input
                ref={fileRef}
                data-testid={SCAN.fileInput}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => onPick(e.target.files?.[0])}
            />
        </div>
    );
}

function AnalyzingStage({ preview }) {
    return (
        <div className="text-center py-6">
            {preview && (
                <img
                    src={preview}
                    alt="Analyzing"
                    className="w-full max-h-[45vh] object-contain rounded-2xl border border-foreground/10 mb-5"
                />
            )}
            <div className="inline-flex items-center gap-2.5 uppercase-mono text-muted-foreground">
                <Sparkle
                    size={16}
                    weight="fill"
                    className="text-[#00FF9D] animate-soft-pulse"
                />
                Reading the machine label & weight pin…
            </div>
        </div>
    );
}

function ReviewStage({ scan, form, setForm, autofillContext }) {
    const ex = scan.extraction;
    const imgSrc = `${process.env.REACT_APP_BACKEND_URL}${scan.image_url}?auth=${encodeURIComponent(tokenStore.get() || '')}`;

    return (
        <div data-testid={SCAN.extractionForm} className="space-y-4">
            <div className="grid sm:grid-cols-[120px_1fr] gap-4 items-start">
                <img
                    src={imgSrc}
                    alt="Captured"
                    className="w-full sm:w-[120px] h-[120px] object-cover rounded-2xl border border-foreground/10"
                />
                <div className="min-w-0">
                    {ex.machine_name && (
                        <p className="uppercase-mono text-muted-foreground mb-1 truncate">
                            Machine · {ex.machine_name}
                        </p>
                    )}
                    <p
                        data-testid={SCAN.confidenceBadge}
                        className="uppercase-mono inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 border"
                        style={{
                            color:
                                ex.confidence >= 0.7
                                    ? '#00FF9D'
                                    : ex.confidence >= 0.4
                                      ? '#FFB347'
                                      : '#FF4D4D',
                            borderColor: 'currentColor',
                            background:
                                ex.confidence >= 0.7
                                    ? 'rgba(0,255,157,0.1)'
                                    : ex.confidence >= 0.4
                                      ? 'rgba(255,179,71,0.1)'
                                      : 'rgba(255,77,77,0.1)',
                        }}
                    >
                        <Sparkle size={12} weight="fill" />
                        {(ex.confidence * 100).toFixed(0)}% match
                    </p>
                    {ex.notes && (
                        <p className="text-xs text-muted-foreground mt-2 line-clamp-3">
                            {ex.notes}
                        </p>
                    )}
                </div>
            </div>

            <Field
                label="Exercise name"
                testId={SCAN.nameInput}
                value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
            />

            <div className="grid grid-cols-2 gap-3">
                <Select
                    label="Category"
                    testId={SCAN.categorySelect}
                    value={form.category}
                    options={CATEGORIES}
                    onChange={(v) => setForm({ ...form, category: v })}
                />
                <Select
                    label="Equipment"
                    testId={SCAN.equipmentSelect}
                    value={form.equipment}
                    options={EQUIPMENT}
                    onChange={(v) => setForm({ ...form, equipment: v })}
                />
            </div>

            <Field
                label="Muscle groups (comma-separated)"
                testId={SCAN.musclesInput}
                value={form.muscles}
                onChange={(v) => setForm({ ...form, muscles: v })}
            />

            {autofillContext && (
                <Field
                    label={`Weight reading (${autofillContext.units || 'kg'}) · auto-fills the next set`}
                    testId={SCAN.weightInput}
                    value={form.weight === null ? '' : String(form.weight)}
                    onChange={(v) => setForm({ ...form, weight: v })}
                    placeholder={
                        ex.weight_reading != null
                            ? `Detected ${ex.weight_reading}`
                            : 'Leave blank if unknown'
                    }
                    inputMode="decimal"
                />
            )}
        </div>
    );
}

function Field({ label, testId, value, onChange, placeholder, inputMode }) {
    return (
        <label className="block">
            <span className="uppercase-mono text-muted-foreground block mb-2">
                {label}
            </span>
            <input
                type="text"
                data-testid={testId}
                value={value}
                inputMode={inputMode}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                className="w-full h-12 rounded-2xl border border-foreground/15 bg-foreground/5 px-4 outline-none focus:border-foreground/40 transition-colors placeholder:text-muted-foreground/70"
            />
        </label>
    );
}

function Select({ label, testId, value, onChange, options }) {
    return (
        <label className="block">
            <span className="uppercase-mono text-muted-foreground block mb-2">
                {label}
            </span>
            <select
                data-testid={testId}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full h-12 rounded-2xl border border-foreground/15 bg-foreground/5 px-4 outline-none focus:border-foreground/40 text-foreground"
            >
                {options.map((o) => (
                    <option key={o} value={o}>
                        {o}
                    </option>
                ))}
            </select>
        </label>
    );
}

function Footer({ stage, canAnalyze, canCommit, committing, onAnalyze, onCommit, onRetake, onCancel }) {
    return (
        <div className="p-4 sm:p-5 border-t border-foreground/10 flex items-center justify-between gap-3">
            {stage === 'review' ? (
                <button
                    type="button"
                    onClick={onRetake}
                    data-testid={SCAN.retakeBtn}
                    className="h-12 px-5 rounded-2xl border border-foreground/15 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.98] transition-all font-semibold inline-flex items-center gap-2"
                >
                    <ArrowCounterClockwise size={16} weight="bold" />
                    Retake
                </button>
            ) : (
                <button
                    type="button"
                    onClick={onCancel}
                    data-testid={SCAN.cancelBtn}
                    className="h-12 px-5 rounded-2xl border border-foreground/15 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.98] transition-all font-semibold"
                >
                    Cancel
                </button>
            )}

            {stage === 'pick' && (
                <button
                    type="button"
                    onClick={onAnalyze}
                    disabled={!canAnalyze}
                    data-testid={SCAN.analyzeBtn}
                    className={`flex-1 h-12 rounded-2xl font-bold tracking-tight transition-all inline-flex items-center justify-center gap-2 ${
                        canAnalyze
                            ? 'bg-primary text-primary-foreground shadow-neon active:scale-[0.98] hover:-translate-y-[1px]'
                            : 'bg-foreground/10 text-foreground/40 cursor-not-allowed'
                    }`}
                >
                    <Sparkle size={16} weight="fill" />
                    Analyze
                </button>
            )}
            {stage === 'analyzing' && (
                <button
                    type="button"
                    disabled
                    className="flex-1 h-12 rounded-2xl bg-foreground/10 text-foreground/60 font-bold tracking-tight inline-flex items-center justify-center gap-2"
                >
                    <Sparkle size={16} weight="fill" className="animate-soft-pulse" />
                    Analyzing…
                </button>
            )}
            {stage === 'review' && (
                <button
                    type="button"
                    onClick={onCommit}
                    disabled={!canCommit}
                    data-testid={SCAN.commitBtn}
                    className="flex-1 h-12 rounded-2xl bg-primary text-primary-foreground font-bold tracking-tight shadow-neon active:scale-[0.98] disabled:opacity-50 transition-all inline-flex items-center justify-center gap-2"
                >
                    <Check size={16} weight="bold" />
                    {committing ? 'Saving…' : 'Save exercise'}
                </button>
            )}
        </div>
    );
}
